import React, { useEffect, useMemo, useState } from "react";
import "./leads-log-filters.scss";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import { camelCase } from "lodash";
import {
  leadsLogFilterOptions as defaultFilterOptions,
  INTERESTED_PRODUCT_OPTIONS,
  LEAD_STATUS_OPTIONS,
} from "./leads-log-filter-helper";
import dayjs from "dayjs";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import LeadValueFilter from "./leads-log-filter-options/lead-value-filter";
import OnboardedDateFilter from "../../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import SubscriptionPlanFilter from "../../../tenant/tenant-list/tenant-filter-options/subscription-plan";
import { useLogin } from "../../../../common/hooks/useUser";
import CompanyFilter from "./leads-log-filter-options/company-filter/company-filter";

const LeadsLogFilters: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: any;
}> = ({ open, onClose, onApplyFilters, filters, companyNames }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    defaultFilterOptions.map((opt) => ({ ...opt }))
  );

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Date Assigned") {
            setFieldValue("onBoardedAtStart", null);
            setFieldValue("onBoardedAtEnd", null);
          } else if (label === "Lead Value") {
            setFieldValue("minValue", null);
            setFieldValue("maxValue", null);
          } else {
            const key = camelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);
  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Date Assigned") {
        values["onBoardedAtStart"] = null;
        values["onBoardedAtEnd"] = null;
      } else if (option.label === "Lead Value") {
        values["leadValueMin"] = null;
        values["leadValueMax"] = null;
      } else {
        const key = camelCase(option.label);
        if (option.label === "Lead Status" || option.label === "Products") {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");
    filterOptions.forEach((option) => {
      if (option.value) {
        if (option.label === "Date Assigned") {
          const startDate = values["onBoardedAtStart"]
            ? dayjs(values["onBoardedAtStart"])
            : null;
          const endDate = values["onBoardedAtEnd"]
            ? dayjs(values["onBoardedAtEnd"])
            : null;

          if (!startDate) {
            errors["onBoardedAtStart"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["onBoardedAtStart"] = "Invalid date";
          }

          if (!endDate) {
            errors["onBoardedAtEnd"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["onBoardedAtEnd"] = "Invalid date";
          }
        } else if (option.label === "Lead Value") {
          const minValue = Number(values["leadValueMin"]);
          const maxValue = Number(values["leadValueMax"]);
          if (minValue && maxValue && minValue > maxValue) {
            errors["leadValueMin"] = "Min value should be less than max value";
          }
          if (!minValue) {
            errors["leadValueMin"] = "Min value is required";
          }
          if (minValue && maxValue && maxValue < minValue) {
            errors["leadValueMax"] =
              "Max value should be greater than min value";
          }
          if (!maxValue) {
            errors["leadValueMax"] = "Max value is required";
          }
          if (minValue < 0) {
            errors["leadValueMin"] = "Min value should be greater than 0";
          }
          if (maxValue < 0) {
            errors["leadValueMax"] = "Max value should be greater than 0";
          }
          if (minValue && maxValue && minValue === maxValue) {
            errors["leadValueMin"] =
              "Min value should not be equal to max value";
          }
          if (minValue && !maxValue) {
            errors["leadValueMax"] = "Max value is required";
          }
          if (maxValue && !minValue) {
            errors["leadValueMin"] = "Min value is required";
          }
        } else if (option.label === "Assigned Partner") {
          if (
            !values.assignedToPartnerId ||
            values.assignedToPartnerId.length === 0
          ) {
            errors["assignedToPartnerId"] = "Assigned Partner cannot be empty";
          }
        } else {
          const key = camelCase(option.label);
          if (
            !values[key] ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            errors[key] = `${option.label} cannot be empty`;
          }
        }
      }
    });

    return errors;
  };

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Date Assigned") {
            acc["onBoardedAtStart"] = values["onBoardedAtStart"];
            acc["onBoardedAtEnd"] = values["onBoardedAtEnd"];
          } else if (curr.label === "Lead Value") {
            acc["leadValueMin"] = values["leadValueMin"];
            acc["leadValueMax"] = values["leadValueMax"];
          } else if (curr.label === "Assigned Partner") {
            acc["assignedToPartnerId"] = values["assignedToPartnerId"];
          } else {
            const key = camelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void,
    handleBlur?: (e: any) => void
  ) => {
    switch (label) {
      case "Date Assigned":
        return (
          <OnboardedDateFilter
            startDate={values.onBoardedAtStart}
            endDate={values.onBoardedAtEnd}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("onBoardedAtStart", start);
              if (end) setFieldValue?.("onBoardedAtEnd", end);
            }}
            startDateError={
              touched?.onBoardedAtStart && errors?.onBoardedAtStart
                ? String(errors.onBoardedAtStart)
                : undefined
            }
            endDateError={
              touched?.onBoardedAtEnd && errors?.onBoardedAtEnd
                ? String(errors.onBoardedAtEnd)
                : undefined
            }
          />
        );
      case "Lead Status":
        return (
          <SubscriptionPlanFilter
            value={
              Array.isArray(values["leadStatus"]) ? values["leadStatus"] : []
            }
            plans={LEAD_STATUS_OPTIONS}
            label="Select Status"
            error={errors?.leadStatus}
            onChange={(val: string[]) => setFieldValue?.("leadStatus", val)}
            isMultiple={true}
          />
        );
      case "Products":
        return (
          <SubscriptionPlanFilter
            value={Array.isArray(values["products"]) ? values["products"] : []}
            plans={INTERESTED_PRODUCT_OPTIONS}
            label="Select Products"
            error={errors?.products}
            onChange={(val: string[]) => setFieldValue?.("products", val)}
            isMultiple={true}
          />
        );
      case "Lead Value":
        return (
          <LeadValueFilter
            minValue={values.leadValueMin}
            maxValue={values.leadValueMax}
            onValueChange={(e: any, type: "min" | "max") => {
              if (type === "min") {
                setFieldValue?.("leadValueMin", e.target.value);
              } else {
                setFieldValue?.("leadValueMax", e.target.value);
              }
            }}
            minError={touched?.leadValueMin && errors?.leadValueMin}
            maxError={touched?.leadValueMax && errors?.leadValueMax}
          />
        );
      case "Assigned Partner":
        return (
          <CompanyFilter
            options={companyNames}
            value={values.assignedToPartnerId}
            onChange={(selected: (number | string)[] | null) =>
              setFieldValue?.("assignedToPartnerId", selected || [])
            }
            handleBlur={() =>
              handleBlur?.({ target: { name: "assignedToPartnerId" } })
            }
            isShowError={
              touched?.assignedToPartnerId && !!errors?.assignedToPartnerId
            }
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = defaultFilterOptions.map((opt) => {
      const key =
        opt.label === "Date Assigned"
          ? "onBoardedAtStart" in filters || "onBoardedAtEnd" in filters
          : opt.label === "Lead Value"
            ? "leadValueMin" in filters || "leadValueMax" in filters
            : opt.label === "Assigned Partner"
              ? "assignedToPartnerId" in filters
              : camelCase(opt.label) in filters;

      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`leads-log-filters ${open ? "open" : ""}`}>
      <div className="leads-log-filters-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>
      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => {
          return (
            <Form className="leads-log-drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) =>
                option.label === "Assigned Partner" && !isSuperParter ? null : (
                  <div key={option.label} className="filter-option-row">
                    <MenuItem
                      key={option.label}
                      value={option.label}
                      className="filter-menu-item"
                      onClick={() =>
                        handleCheckboxChange(
                          option.label,
                          values,
                          setFieldValue
                        )
                      }
                    >
                      <Checkbox checked={option.value} />
                      <ListItemText primary={option.label} />
                    </MenuItem>

                    {option.value &&
                      renderFilterField(
                        option.label,
                        values,
                        errors,
                        touched,
                        setFieldValue
                      )}
                  </div>
                )
              )}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        defaultFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({}, false);
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default LeadsLogFilters;
